import { ElementRef, ChangeDetectorRef, Input, Component, OnInit, ChangeDetectionStrategy, NgZone, EventEmitter, Output, OnDestroy } from '@angular/core';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { Observable } from 'rxjs/Observable';
import { distinctUntilChanged, filter, map, pluck, takeUntil, tap } from 'rxjs/operators';
import { IResource } from '@ispm/core/interfaces/resource.interface';
import { usersTypes } from '@ispm/user/constants/usersTypes.enum';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { IPrioritySection } from '@ispm/therapies/interfaces/view-configuration.interface';
import { Subject } from 'rxjs';

@Component({
  selector: 'gen-treatment-selection',
  templateUrl: './treatment-selection.component.html',
  styleUrls: ['./treatment-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreatmentSelectionComponent implements OnInit, OnDestroy {
  @Input()
  public revisionId: string;

  @Input()
  public workflowId: string;

  @Input()
  public sectionConfiguration: IPrioritySection;

  @Input()
  public isEditable: boolean;

  @Input()
  public signOffAvailable: boolean;

  @Output()
  public updateInprogress: EventEmitter<boolean> = new EventEmitter<boolean>();

  public activeReview: any = null;

  public userType: usersTypes = usersTypes.PRACTITIONER;

  public previousReviews$: Observable<any>;

  public currentUserId$: Observable<string>;

  public currentIndex: number = null;

  public amountOfReviews: number;

  private currentUserRole$: Observable<string>;

  private destroyer: Subject<null> = new Subject<null>();


  constructor(private therapiesAC: TherapiesActionCreatorService,
              private cd: ChangeDetectorRef,
              private _zone: NgZone,
              private elementRef: ElementRef,
              private workflowAC: WorkflowConfigService,
              private userAC: UserActionCreatorService) {
  }

  static setReviewPractitionerId(review) {
    if (review['practitionerId']) {
      return;
    }

    const practitionerCTX = review.context.find(ctx => ctx.resourceType.toLowerCase() === 'practitioner');
    if (practitionerCTX) {
      review['practitionerId'] = practitionerCTX.reference;
    }
  }

  ngOnInit() {
    this.currentUserId$ = this.userAC.getCurrentUser().pipe(pluck('id'));
    this.currentUserRole$ = this.userAC.getCurrentUser().pipe(pluck('role'));
    this.previousReviews$ = this.therapiesAC.getReviews()
      .pipe(
        takeUntil(this.destroyer.asObservable()),
        distinctUntilChanged(),
        filter(reviews => !!reviews),
        tap((reviews: Array<IResource>) => this.amountOfReviews = this.isEditable || reviews.length === 0 ? reviews.length : reviews.length - 1),
        map((reviews: Array<IResource>) => {
          reviews.forEach((review) => {
            TreatmentSelectionComponent.setReviewPractitionerId(review);
          });
          if (this.currentIndex === null) {
            this._zone.runOutsideAngular(() => {
              setTimeout(() => {
                this.navigateTo(reviews.length);
                this.setActiveReview(reviews[this.currentIndex]);
              }, 0);
            });
          }
          return reviews;
        })
      );

  }

  trackByFn(review) {
    return review && review.id;
  }


  getCountText(count: number) {
    switch (count) {
      case 1:
        return '1st';
      case 2:
        return '2nd';
      case 3:
        return '3rd';
      default:
        return `${count}th`;
    }
  }

  setActiveReview(review) {
    this.activeReview = review;
    this.cd.markForCheck();
  }

  navigateTo(number) {
    this.currentIndex += number;
    if (this.currentIndex < 0) {
      this.currentIndex = 0;
    } else if (this.currentIndex > this.amountOfReviews) {
      this.currentIndex = this.amountOfReviews;
    }
    const element = this.elementRef.nativeElement.querySelector('.reviewers');
    const elementChildren = element && element.children;
    if (elementChildren && elementChildren.length > this.currentIndex) {
      elementChildren[this.currentIndex].scrollIntoView({ behavior: 'smooth'});
    }
    this.cd.markForCheck();
    this.cd.detectChanges();
  }

  reviewsAreEqual(review) {
    if (!review || !this.activeReview) {
      return review === this.activeReview;
    } else {
      return review.id === this.activeReview.id;
    }
  }

  emitInprogressUpdate(inprogress: boolean) {
    this.updateInprogress.emit(inprogress);
  }

  ngOnDestroy(): void {
    this.destroyer.next();
    this.destroyer.complete();
  }
}
